import {
    BulkRegPointRequest,
    CreatePointRequest,
    CreateTagRequest,
    CreateTopicRequest,
    CreateTopicResponse,
    DiscoveryLevel,
    GetTopicResponse,
    Location,
    TopicCategory,
    TopicCreationSource
} from '../../types';
import { LitElement, css, html } from 'lit';
import { addTopic, getTopic } from '../../helpers/api/topics-api';
import { customElement, property, query, state } from 'lit/decorators';

import { RegAddPointComponent } from './reg-add-point-component';
import { TextArea } from '@material/mwc-textarea';
import { TextField } from '@material/mwc-textfield';
import { TopicCenterInput } from '../common/topic-center-input';
import { addPoint } from '../../helpers/api/points-api';
import { parseIntOrUndefined } from '../../helpers/utils';
import { validateEmail } from '../../helpers/strings';
import { getMainPage } from '../../helpers/page-name';

@customElement('bulk-reg-component')
export class BulkRegComponent extends LitElement {
    static amplitude_callsite: string = 'Bulk Reg Component';


    @query('#addPoint')
    regAddPointElement!: RegAddPointComponent;

    @query('#mapTitle')
    private readonly mapTitleElement!: TextField;

    @query('#emails')
    private readonly emailElement!: TextArea;

    @query('#topicCenterInput')
    private readonly topicCenterElement!: TopicCenterInput;

    @property()
    templateName = '<template name>';

    @property({ type: Array })
    templateCategories: TopicCategory[] = [];

    @state()
    pointsToAdd: BulkRegPointRequest[] = [];

    @state()
    emails: string[] = [];

    private readonly emailToTopicIdMap: Map<string, string> = new Map();
    private readonly topicIndex: Map<string, GetTopicResponse> = new Map();
    private readonly topicKeyIndex: Map<string, string> = new Map();
    private readonly topicCategoryIndex: Map<string, Map<string, string>> = new Map();

    render() {
        return html`
            <div id="mainContainer">
                <div class="centeredFlexItem">
                    <h3>
                        Welcome to your
                        <span id="mapTemplateName">${this.templateName}</span> Map!
                    </h3>
                </div>
                <div class="centeredFlexItem">To get started, we need a few things.</div>
                <div class="inputContainer">
                    <mwc-textfield
                        id="mapTitle"
                        class="input"
                        maxlength="35"
                        required
                        label="Map Title"
                    ></mwc-textfield>
                </div>
                <div class="inputContainer">
                    <topic-center-input id="topicCenterInput"></topic-center-input>
                </div>
                <div class="inputContainer">
                    <mwc-textarea
                        id="emails"
                        class="input"
                        required
                        label="Administrator Email Addresses (comma-separated)"
                        @input="${this.updateAdminEmails}"
                    >
                    </mwc-textarea>
                </div>
                <mwc-button id="formButton" class="centeredFlexItem" @click="${this.save}" raised
                    >Submit</mwc-button
                >
                <hr />
                <reg-add-point-component
                    id="addPoint"
                    .categories="${this.templateCategories}"
                    .emails="${this.emails}"
                >
                </reg-add-point-component>
                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    static get styles() {
        return css`
            #mainContainer {
                display: flex;
                flex-direction: column;
                justify-content: center;
                max-width: 800px;
            }

            .input {
                width: 100%;
            }

            #mapInputContainer {
                display: flex;
                flex-direction: row;
                width: 100%;
            }

            .inputContainer {
                margin-top: 20px;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            }

            .inputTextField {
                margin-left: 32px;
                margin-top: 5px;
                width: 100%;
            }

            .pointEntry {
                display: inline-flex;
                width: 100%;
            }

            #formButton {
                --mdc-theme-primary: var(--main-primary-color);
                --mdc-theme-on-primary: var(--theme-white-color);
                margin-top: 32px;
                min-width: 60%;
            }

            .centeredFlexItem {
                align-self: center;
            }

            #mapTemplateName {
                font-style: italic;
                font-size: larger;
            }

            @media only screen and (max-width: 768px) {
                /* For mobile phones: */
                #mapInputContainer {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                }
            }
        `;
    }

    private updateAdminEmails() {
        if (this.emailElement?.reportValidity()) {
            this.emails = this.emailElement.value
                .toLowerCase()
                .split(',')
                .map((s) => s.trim());
        }
    }

    private async save() {
        const nameValid = this.mapTitleElement.reportValidity();
        const emails = this.emailElement.value
            .toLowerCase()
            .split(',')
            .map((s) => s.trim());
        const emailValid = emails.every((email) => validateEmail(email));
        const hasCategories = this.templateCategories.length !== 0;

        const valid = nameValid && emailValid && hasCategories;

        if (!valid) {
            getMainPage().errorToastElement.showToastAndThrow(
                'Please fix your invalid inputs (marked red).'
            );
        }

        if (!emailValid) {
            getMainPage().errorToastElement.showToastAndThrow('Invalid email format.');
        }

        const name = this.mapTitleElement.value.trim();

        // It is OK for these to be undefined, since the server sets a default.
        if (!this.topicCenterElement.defaultCenterIsValid) {
            getMainPage().errorToastElement.showToastAndThrow(
                'The center of map location is invalid (marked red).'
            );
        }
        const default_map_center = this.topicCenterElement.defaultCenterValue;
        const default_map_zoom = parseIntOrUndefined(
            this.topicCenterElement.defaultZoomValue
        );

        const createTopicCalls = emails.map(async (email) => {
            await this.createTopic(name, email, default_map_center, default_map_zoom);
        });

        await Promise.all(createTopicCalls);

        getMainPage().errorToastElement.showToast(`Successfully created ${this.emails.length} topics`);
        await this.submitPoints();
    }

    private async createTopic(
        name: string,
        email: string,
        default_map_center: Location,
        default_map_zoom: number | undefined
    ) {
        const tag_info: CreateTagRequest = {
            user_tags: [],
        };

        const createTopicRequest: CreateTopicRequest = {
            name,
            email,
            organization_id: undefined,
            use_organization_template: false,
            creator: undefined,
            categories: this.templateCategories,
            has_dates: false,
            should_filter_past_events: false,
            has_likes: false,
            discovery_level: DiscoveryLevel.Private,
            disable_google_images: false,
            hide_header: false,
            show_advanced_filter_row: false,
            custom_font: undefined,
            requires_approval: false,
            send_crowdsource_email: true,
            image_display: undefined,
            default_list_view: undefined,
            default_map_center,
            default_map_zoom,
            default_map_rotation: 0,
            default_map_tilt: 0,
            header_logo: undefined,
            header_url: undefined,
            header_color: undefined,
            header_text_color: undefined,
            cluster_accent_color: undefined,
            translation_languages: undefined,
            search_pill_settings: undefined,
            share_settings: undefined,
            featured_color: undefined,
            featured_name: undefined,
            custom_map_geojson: undefined,
            custom_map_geojson_style: undefined,
            custom_map_style: undefined,
            custom_map_style_name: undefined,
            custom_shapes: undefined,
            custom_overlays: undefined,
            custom_social_media_image: undefined,
            marker_cluster_minimum: undefined,
            marker_cluster_threshold: undefined,
            icon_size: undefined,
            show_easy_date_filters: undefined,
            tag_info,
            pop_ups: undefined,
            comment_settings: undefined,
            add_point_customization: undefined,
            creation_source: TopicCreationSource.INTERNAL_CREATE,
            point_email_capture_customization: undefined,
        };

        let createTopicResponse: CreateTopicResponse;
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            createTopicResponse = await addTopic(
                BulkRegComponent.amplitude_callsite,
                createTopicRequest,
                { template_name: this.templateName }
            );
        }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const topicKey = createTopicResponse!.write_key;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const topicId = createTopicResponse!._id.$oid;

        // Fetch the topic so we have access to the categories
        await this.fetchTopic(topicId, topicKey);
    }

    private async fetchTopic(topicId: string, topicKey: string) {
        let topic: GetTopicResponse | undefined;
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            topic = await getTopic(
                BulkRegComponent.amplitude_callsite,
                topicId,
                topicKey,
                false
            );
        }, 'An error has occurred. Context: could not get the topic from the server.');

        if (topic === undefined) {
            getMainPage().errorToastElement.showToastAndThrow(
                'An error has occurred.  Context: could not get the topic from the server.'
            );
            return;
        }

        if (topic.email === undefined) {
            getMainPage().errorToastElement.showToastAndThrow(
                'An error has occurred.  Context: Bulk reg requires emails to be provided at this time.'
            );
            return;
        }

        this.emailToTopicIdMap.set(topic.email, topicId);
        this.topicIndex.set(topicId, topic);
        this.topicKeyIndex.set(topicId, topicKey);

        this.topicCategoryIndex.set(topicId, new Map());
        topic.categories.forEach((cat) => {
           this.topicCategoryIndex.get(topicId)?.set(cat.name, cat._id?.$oid ?? '');
        });
    }

    private async submitPoints() {
        const pointRequests: BulkRegPointRequest[] = this.regAddPointElement.pointsToAdd;

        // Now use the indexes to create points for each topic. Though the points will have the same visual data,
        // they will be assigned to different category IDs (which in turn are different for each topic, hence the need for the indexes)
        const pointAddCalls = pointRequests.map(async (regPointRequest) => {
            const point = regPointRequest as CreatePointRequest;

            if (point.email === undefined) {
                getMainPage().errorToastElement.showToastAndThrow(
                    'An error has occurred.  Context: Bulk reg requires emails to be provided at this time.'
                );
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const topicId = this.emailToTopicIdMap.get(point.email ?? '')!;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const addPointKey = this.topicKeyIndex.get(topicId)!;

            // We assume that category names are unique. This should be fine since they are created by a Proxi employee.
            const categoryId = this.topicCategoryIndex.get(topicId)?.get(regPointRequest.catName);
            point.topic_category_id = { $oid: categoryId ?? '' };
            point.topic_id = { $oid: topicId };

            return await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                await addPoint(
                    BulkRegComponent.amplitude_callsite,
                    topicId,
                    point,
                    addPointKey
                );
            }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');
        });

        await Promise.all(pointAddCalls);

        getMainPage().errorToastElement.showToast(
            `Successfully added ${pointRequests.length} points across ${this.emails.length} topics.`
        );
    }
}
